import React, { useRef, useState, useCallback, useEffect } from "react";
import { Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
    DmsAltBox,
    DmsButton,
    DmsButtonGroup,
    DmsPage,
    DmsPermissions,
    DmsTable,
    DmsFlexContainer,
    intl,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Permissions } from "../../constants/Permissions";
import { ExpandableChipList } from "../../components/ExpandableChipList";
import { RoleFormModal } from "../../components/RoleFormModal";

export const RolesPage = () => {
    const table = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [permanentRoles, setPermanentRoles] = useState([]);

    useEffect(() => {
        const loadPermanentRoles = async () => {
            try {
                const response = await fetchData("/role-config/permanent-roles", "GET", true);
                const data = response?.data?.data || response?.data || [];
                setPermanentRoles(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Failed to load permanent roles:", error);
                setPermanentRoles([]);
            }
        };
        loadPermanentRoles();
    }, []);

    const handleOpenCreate = useCallback(() => {
        setSelectedRole(null);
        setModalOpen(true);
    }, []);

    const handleOpenEdit = useCallback((role) => {
        setSelectedRole(role);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedRole(null);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
    }, []);

    const handleDelete = useCallback(async (role) => {
        const translatedRoleName = intl.get(`RoleName.${role.role_name}`).d(role.role_name);
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmDelete").d("Confirm Delete"),
            text: intl.get("DmsPageRole.DeleteButtonMessage", { roleName: translatedRoleName })
                .d("The role {roleName} will be deleted. Are you sure about it?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(`/role/${role.pk}`, "DELETE", true);
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("RoleDeletedSuccessfully").d("Role deleted successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
            } catch (error) {
                console.error("Failed to delete role:", error);
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: intl.get("FailedToDeleteRole").d("Failed to delete role"),
                });
            }
        }
    }, []);

    const translateRoleName = (roleName) => {
        return intl.get(`RoleName.${roleName}`).d(roleName);
    };

    const tableHeader = [
        {
            label: intl.get("Name").d("Name"),
            column_name: "role_name",
            filter: true,
            element: (row) => translateRoleName(row.role_name),
        },
        {
            label: intl.get("Permissions").d("Permissions"),
            column_name: "permissions",
            filter: false,
            sortable: false,
            element: (row) => (
                <ExpandableChipList
                    items={row.permissions || []}
                    labelKey="permission_name"
                    maxVisible={2}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            label: intl.get("Actions").d("Actions"),
            permissions: [Permissions.CAN_EDIT_ROLE],
            column_name: "actions",
            width: "120px",
            sortable: false,
            filter: false,
            element: (row) => {
                const isPermanent = permanentRoles?.includes(row.role_name);
                return (
                    <DmsPermissions permissions={[Permissions.CAN_EDIT_ROLE]}>
                        <DmsButtonGroup>
                            <Tooltip title={intl.get("Edit").d("Edit")}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(row)}
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            {!isPermanent && (
                                <Tooltip title={intl.get("Delete").d("Delete")}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(row)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </DmsButtonGroup>
                    </DmsPermissions>
                );
            },
        },
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={[Permissions.CAN_EDIT_ROLE]}>
                    <DmsButton
                        icon={<AddIcon />}
                        text={intl.get("CreateRole").d("Create Role")}
                        onClick={handleOpenCreate}
                        variant="primary"
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <DmsPage name="Roles">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("Roles").d("Roles")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={tableHeader}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={table}
                        url="/role/table"
                        toolbar={toolbar}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <RoleFormModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                role={selectedRole}
                isPermanent={selectedRole ? permanentRoles.includes(selectedRole.role_name) : false}
            />
        </DmsPage>
    );
};
